<template>
	<div class="touch-card-simple-list">
		<div v-if="!rows.length" class="text-center pa-6 text-disabled">{{ noDataText ?? t('NoData') }}</div>

		<div v-else class="touch-card-simple-list__cards">
			<div v-for="(row, index) in rows" :key="getRowKey(row, index)" class="touch-card-simple-list__card">
				<div v-if="badge(row)" class="touch-card-simple-list__badge">{{ badge(row) }}</div>

				<div class="touch-card-simple-list__main">
					<div class="touch-card-simple-list__header">
						<div class="touch-card-simple-list__heading">
							<div class="title">{{ title(row) }}</div>
							<div v-if="subtitle(row)" class="subtitle">{{ subtitle(row) }}</div>
						</div>
						<v-icon v-if="actionIcon" class="touch-card-simple-list__action" :class="actionColorClass" :aria-label="actionLabel" @click="emit('action', row)">{{ actionIcon }}</v-icon>
					</div>
					<div v-if="chip(row) || body(row)" class="touch-card-simple-list__meta">
						<v-chip v-if="chip(row)" size="small" variant="tonal" density="comfortable" color="secondary" readonly>{{ chip(row) }}</v-chip>
						<span v-if="body(row)" class="text-caption text-medium-emphasis touch-card-simple-list__meta-text">{{ body(row) }}</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed } from 'vue';
import { useTranslator } from '@/composables/useTranslator';

const t = useTranslator();

const props = defineProps({
	rowData: { type: Array, default: () => [] },
	titleField: { type: [String, Array], default: 'label' },
	subtitleFields: { type: Array, default: () => [] },
	badgeField: { type: String, default: null },
	chipField: { type: String, default: null },
	bodyFields: { type: Array, default: () => [] },
	rowKey: { type: String, default: 'id' },
	actionIcon: { type: String, default: null },
	actionColor: { type: String, default: null },
	actionLabel: { type: String, default: null },
	noDataText: { type: String, default: null },
});

const emit = defineEmits(['action']);

const rows = computed(() => (Array.isArray(props.rowData) ? props.rowData : []));
const titleFields = computed(() => (Array.isArray(props.titleField) ? props.titleField : [props.titleField]).filter(Boolean));
const actionColorClass = computed(() => (props.actionColor ? `text-${props.actionColor}` : null));

function getValue(row, field) {
	return row && field ? (row[field] ?? '') : '';
}

function getRowKey(row, index) {
	return row?.[props.rowKey] ?? index;
}

function title(row) {
	const field = titleFields.value.find((candidate) => getValue(row, candidate) !== '');
	return field ? getValue(row, field) : '';
}

function subtitle(row) {
	return props.subtitleFields
		.map((field) => getValue(row, field))
		.filter((value) => value !== '' && value !== null && value !== undefined)
		.join(' · ');
}

function badge(row) {
	return getValue(row, props.badgeField);
}

function chip(row) {
	return getValue(row, props.chipField);
}

function body(row) {
	return props.bodyFields
		.map((field) => getValue(row, field))
		.filter((value) => value !== '' && value !== null && value !== undefined)
		.join(' · ');
}
</script>

<style scoped lang="scss">
@use '@/styles/variables' as *;

.touch-card-simple-list {
	&__cards {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	&__card {
		display: flex;
		align-items: center;
		gap: 12px;
		border: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
		border-radius: 12px;
		background: rgb(var(--v-theme-surface));
		padding: 10px 12px;
		animation: touch-card-simple-list-fade-in 0.28s ease both;
	}

	&__badge {
		flex: none;
		align-self: center;
		padding: 4px 8px;
		border: solid 1px rgb(var(--v-theme-primary));
		color: rgb(var(--v-theme-primary));
		border-radius: $card-border-radius;
		font-size: $font-size-tiny;
		font-weight: 600;
		white-space: nowrap;
	}

	&__main {
		flex: 1 1 auto;
		min-width: 0;
	}

	&__header {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 8px;
	}

	&__heading {
		display: flex;
		flex-direction: column;
		min-width: 0;
	}

	&__meta {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-top: 4px;
		min-width: 0;
	}

	&__meta-text {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__action {
		cursor: pointer;
		flex: none;
	}

	.title {
		font-size: $font-size;
		line-height: $font-size-extra-large;
		font-weight: 600;
		white-space: nowrap;
		text-overflow: ellipsis;
		overflow: hidden;
	}

	.subtitle {
		font-size: $font-size-tiny;
		line-height: $font-size;
		color: rgba(var(--v-theme-on-surface), 0.6);
		white-space: nowrap;
		text-overflow: ellipsis;
		overflow: hidden;
	}
}

@keyframes touch-card-simple-list-fade-in {
	from {
		opacity: 0;
		transform: translateY(8px);
	}

	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@media (prefers-reduced-motion: reduce) {
	.touch-card-simple-list__card {
		animation: none;
	}
}
</style>
